//External
import { Injectable } from '@angular/core';
import { UrlTree } from '@angular/router';
import { catchError, map, mergeMap, Observable, of, Subject, switchMap, takeUntil, forkJoin, take } from 'rxjs';

// Internal
import { ModalName, ModalRoutelessService } from '../../../../common/components/ui/ui-modal-routeless/modal.routeless.service';
import { AppsConfigService } from '../../../../common/config/apps.config.service';
import { ConfigService } from '../../../../common/config/config.service';
import { UtilsCommonService } from '../../../../common/utils/utils-common.service';
import { ReferralValuesService } from '../../../../common/values/referral.values.service';
import { QueryParamsValues, SubscriptionsValuesService, SubscriptionStatus } from '../../../../common/values/subscriptions.values.service';
import { ProductsToInstall, ValuesService } from '../../../../common/values/values.service';
import { FeedbackModalScenarios } from '../../../../common/values/ato.values.service';
import { MessageService } from '../../core/message.service';
import { QueryParamsService } from '../../core/query.params.service';
import { UrlProperties } from '../../guards/models/UrlProperties.model';
import { DevicesService } from '../../process/devices/devices.service';
import { ProfilesService } from '../../process/profiles/profiles.service';
import { SettingsService } from '../../process/settings/settings.service';
import { RedeemService } from '../../process/subscriptions/redeem.service';
import { SubscriptionsService } from '../../process/subscriptions/subscriptions.service';
import { SubscriptionsTrialService } from '../../process/subscriptions/subscriptionTrial.service';
import { ConnectReferralService } from '../../requests/connect-referral/connect-referral.service';
import { False, ModalAction } from './models/special.cases.model';
import { SubscriptionInviteService } from '../../requests/subscription-invite-service/subscription-invite.service';
import { InvitesService } from '../../process/subscriptions/invites.service';
import { CommercialIdsMgmtService } from '../../requests/connect-commercial-ids-service/connect-commercial-ids.service';
import { BundleModel, RedeemDataModel } from '../../../models/Services.model';
import { ConversionStatus, SecuredPaymentScenarios } from '../../../models/subscriptions/Subscriptions.model';
import { ConnectGroupMgmtService } from '../../requests/connect-group-mgmt/connect-group-mgmg.service';
import { AckInviteResponse, AckInviteResponseWrapper, ContextService, SwitchContextResponse } from '../../global/context/context.service';
import { BrowserBooleanValues } from '../../../../common/models/Core.model';
import { GetConversionResponse } from '../../../../common/models/Onboarding.model';
import {
    ModalSize,
    AllModalInformation,
    ModalContainerOptions,
    ModalContentOptionsError
} from '../../global/modal/Modal.model';
import {
    ContextBubbleLocalStorage,
    CreateGroupResponse,
    FamilyPlanService,
    Group,
    GroupRoles,
    GroupTypes,
    OpenInstallModal
} from '../../../../common/models/subscriptions/Groups.model';
import { SuperiorTrialKeys, SuperiorTrialResponse } from '../../../../common/models/SuperiorTrial.model';
import { ISubscriptionsService } from '../../interfaces/isubscriptions/isubscriptions.service';
import { CheckoutSdkFlows } from '../../../models/CheckoutSdk.model';
import { CheckoutSdkService } from '../../core/checkout-sdk.service';
import { GroupManagementService } from '../../process/subscriptions/group-management.service';
import { BucketTestingService } from '../../requests/bucket-testing-service/bucket-testing.service';
import { AdobeDataLayerService } from '../../core/adobe.datalayer.service';

@Injectable({
    providedIn: 'root'
})

export class SpecialCasesService {

    private readonly onDestroy$: Subject<void> = new Subject<void>();

    constructor(
        private readonly profilesService           : ProfilesService,
        private readonly valuesService             : ValuesService,
        private readonly configService             : ConfigService,
        private readonly subscriptionsService      : SubscriptionsService,
        private readonly settingsService           : SettingsService,
        private readonly modalRoutelessService     : ModalRoutelessService,
        private readonly appsConfigService         : AppsConfigService,
        private readonly queryParamsService        : QueryParamsService,
        private readonly connectReferralService    : ConnectReferralService,
        private readonly referralValuesService     : ReferralValuesService,
        private readonly messageService            : MessageService,
        private readonly redeemService             : RedeemService,
        private readonly inviteService             : InvitesService,
        private readonly utilsCommonService        : UtilsCommonService,
        private readonly subscriptionsValuesService: SubscriptionsValuesService,
        private readonly subscriptionsTrialService : SubscriptionsTrialService,
        private readonly subscriptionInviteService : SubscriptionInviteService,
        private readonly commercialIdsMgmtService  : CommercialIdsMgmtService,
        private readonly devicesService            : DevicesService,
        private readonly connectGroupMgmtService   : ConnectGroupMgmtService,
        private readonly contextService            : ContextService,
        private readonly iSubscriptionService      : ISubscriptionsService,
        private readonly checkoutSDKService        : CheckoutSdkService,
        private readonly groupManagementService    : GroupManagementService,
        private readonly bucketTestingService      : BucketTestingService,
        private readonly adobeDataLayerService     : AdobeDataLayerService
    ) {}

    /**
     * This function will decide if the account is MSP type and the specific modals will be shown
     * 1. Daca are msp lvl 1, e pe dashboard si e prima oara pe cont -> afisam onboarding
     * 2. Daca are msp lvl 1, e pe dashboard si nu e prima oara pe cont dar are sub -> afisam install
     * 3. Daca are msp lvl 1, e pe dashboard si nu e prima oara pe cont dar nu are sub -> afisam onboarding
     * 4. Daca are msp lvl 2, e prima oara pe cont si are subscriptie managed -> afisam onboarding de msp
     * 5. Daca are msp lvl 2, e prima oara pe cont si nu are subscriptie managed -> nu afisam nimic
     * @param {UrlProperties} urlProperties
     * @return {*}  {(Observable<boolean|UrlTree>)}
     */
    public _decideMspXspState(urlProperties: UrlProperties): Observable<False|ModalAction> {
        return new Observable( subscriber => {
            const hasMspOrXspLevelOne   = this.profilesService.hasMspOrXspLevelOne();
            const hasMspOrXspLevelTwo   = this.profilesService.hasMspOrXspLevelTwo();
            const firstPathSegment      = `/${urlProperties.pathPieces[0]}`;
            const dashboardPath         = this.valuesService.centralPaths.dashboard.path;
            const hasOnboardingMSP      = this.configService.config.onboardingMSP;
            const hasSubscription       = this.subscriptionsService.getFilteredActiveBundles().length;
            const isFirstLogin          = this.settingsService.isFirstLogin();
            const storageFirstLoginMade = sessionStorage.getItem(this.valuesService.sessionStorageModalStatus.firstLoginMade);

            if (hasMspOrXspLevelOne && firstPathSegment === dashboardPath) {
                if (isFirstLogin && hasOnboardingMSP && !storageFirstLoginMade) {
                    this.settingsService.setBoarded()
                    .pipe(takeUntil(this.onDestroy$))
                    .subscribe()
                    .add(
                        () => {
                            if (!this.subscriptionsService.getNoBundles()) {
                                setTimeout(() => {
                                    this.subscriptionsService.updateSubscriptions();
                                    this.subscriptionsService.list()
                                    .pipe(takeUntil(this.onDestroy$))
                                    .subscribe()
                                    .add(
                                        () => {
                                            this.subscriptionsService.processSubscriptions();
                                            this.subscriptionsService.populateFilteredSubscriptionsStructures();
                                            this.subscriptionsService.setHasZuora();
                                            this.modalRoutelessService.setBufferItem(this.valuesService.modalBufferKeys.MSP_INSTALL_MODAL, true);
                                            subscriber.next(this.openOnboardingMspXspModal());
                                            subscriber.complete();
                                        });
                                }, 5000);
                            } else {
                                subscriber.next(this.openOnboardingMspXspModal());
                                subscriber.complete();
                            }
                        }
                    );
                } else {
                    if (hasSubscription && this.appsConfigService.getShowInstallButtonForAllApps()) {
                        const devicesNo = this.devicesService.getDevicesNr();
                        if (devicesNo) {
                            // When devices exist on account we do not show install protection modal
                            subscriber.next(false);
                            subscriber.complete();
                            return;
                        }

                        if (this.modalRoutelessService.getBufferItem(this.valuesService.modalBufferKeys.MSP_INSTALL_MODAL)) {
                            subscriber.next(false);
                            subscriber.complete();
                            return;
                        }

                        this.modalRoutelessService.setBufferItem(this.valuesService.modalBufferKeys.MSP_INSTALL_MODAL, true);
                        subscriber.next(this.openInstallModal());
                        subscriber.complete();
                    } else {
                        if (urlProperties.pathPieces[0] === this.valuesService.centralPaths.dashboard.id && hasOnboardingMSP) {
                            if (this.modalRoutelessService.getBufferItem(this.valuesService.modalBufferKeys.MSP_ONBOARDING)) {
                                subscriber.next(false);
                                subscriber.complete();
                            } else {
                                this.modalRoutelessService.setBufferItem(this.valuesService.modalBufferKeys.MSP_ONBOARDING, true);
                                subscriber.next(this.openOnboardingMspXspModal());
                                subscriber.complete();
                            }
                        } else {
                            subscriber.next(false);
                            subscriber.complete();
                        }
                    }
                }
            } else if (hasMspOrXspLevelTwo && hasOnboardingMSP && isFirstLogin && !storageFirstLoginMade) {
                this.markOnboardingAsDone()
                .pipe(takeUntil(this.onDestroy$))
                .subscribe(resp => {
                    subscriber.next(resp);
                    subscriber.complete();
                });
            } else {
                subscriber.next(false);
                subscriber.complete();
            }
        });
    }

    /**
     * Mark onboarding as done and decide if onboarding msp modal should be shown
     * @private
     * @memberof SpecialCasesService
     * @returns {Observable}
     */
    private markOnboardingAsDone(): Observable<False|ModalAction> {
        return new Observable(subscriber => {
            this.settingsService.setBoarded()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe()
            .add(() => {
                if (!this.subscriptionsService.getNoBundles()) {
                    setTimeout(() => {
                        this.redoSubscriptionsRequestForOnboarding()
                        .pipe(takeUntil(this.onDestroy$))
                        .subscribe(resp => {
                            subscriber.next(resp);
                            subscriber.complete();
                        });
                    }, 7000);
                } else {
                    const response = this.subscriberValueForOnboarding();
                    subscriber.next(response);
                    subscriber.complete();
                }
            });
        });
    }

    /**
     * Makes 'list' subscriptions request and decide if onboarding msp modal should be shown
     * @private
     * @memberof SpecialCasesService
     * @returns {Observable}
     */
    private redoSubscriptionsRequestForOnboarding(): Observable<False|ModalAction> {
        return new Observable(subscriber => {
            this.subscriptionsService.updateSubscriptions();
            this.subscriptionsService.list()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
                next: () => {
                    this.subscriptionsService.processSubscriptions();
                    this.subscriptionsService.populateFilteredSubscriptionsStructures();
                    this.subscriptionsService.setHasZuora();

                    const response = this.subscriberValueForOnboarding();
                    subscriber.next(response);
                    subscriber.complete();
                },
                error: () => {
                    subscriber.next(false);
                    subscriber.complete();
                }
            });
        });
    }

    /**
     * Decides if onboarding msp modal should be opened
     * @private
     * @memberof SpecialCasesService
     * @returns {AllModalInformation|boolean} Returns an object if the modal should be displayed and 'false' otherwise
     */
    private subscriberValueForOnboarding(): False|AllModalInformation {
        if (this.subscriptionsService.hasMspManagedBundle()) {
            return this.openOnboardingMspXspModal();
        }
        return false;
    }

    /**
     * Computes the modal info for the onboarding msp modal
     * @returns {AllModalInformation} The object containing the modal properties
     */
    private openOnboardingMspXspModal(): AllModalInformation {
        return {
            name: ModalName.onboardingMsp,
            containerOptions: {},
            contentOptions: {}
        } as AllModalInformation;
    }

    /**
     * Computes the modal info for the referral congrats activation modal
     * @param referralResponse The object containing the referral response data
     * @returns {AllModalInformation} The object containing the modal properties
     */
    private openReferralCongratsModal(referralResponse): AllModalInformation {
        return {
            name: ModalName.referralCongratsModal,
            containerOptions: {
                size: ModalSize.SM
            },
            contentOptions: {
                referralResponse,
                case: this.referralValuesService.referralCongratsCase.CONGRATS_ACTIVATE
            }
        } as AllModalInformation;
    }

    /**
     * Computes the modal info for the congrats activation modal, error case, depending on the error code
     * @param error The object containing the error data
     * @returns {AllModalInformation} The object containing the modal properties
     */
    private openReferralErrorModal(error): AllModalInformation {
        let modalCase = this.referralValuesService.referralCongratsCase.ERROR_ACTIVATION;
        // trebuie sa tinem cont si de 'data' pentru ca nu avem un cod diferit pt aceasta eroare
        // nu se poate schimba in connect pentru ca se strica compatibilitatea cu BMS-ul...
        const errorCode = error?.error?.data?.code;
        const errorMessage = error?.error?.data?.data;

        if (errorCode === this.referralValuesService.codeError.code && this.referralValuesService.congratsCaseBasedOnError[errorMessage]) {
            modalCase = this.referralValuesService.congratsCaseBasedOnError[errorMessage];
        }
        return {
            name: ModalName.referralCongratsModal,
            containerOptions: {
                size: ModalSize.SM
            },
            contentOptions: {
                case: modalCase
            }
        } as AllModalInformation;
    }

    public _decideReferral(): Observable<boolean|any> {
        return new Observable( subscriber => {
            const referralCode = this.queryParamsService.get(this.valuesService.queryParams.referralCode);
            if (referralCode) {
                this.connectReferralService.applyReferral(referralCode)
                .pipe(takeUntil(this.onDestroy$))
                .subscribe({
                    next: resp => {
                        this.subscriptionsService.updateSubscriptions();
                        this.messageService.sendMessage(this.valuesService.events.triggerStart, {});
                        this.queryParamsService.remove([this.valuesService.queryParams.referralCode]);
                        subscriber.next(this.openReferralCongratsModal(resp));
                        subscriber.complete();
                    },
                    error: err => {
                        this.queryParamsService.remove([this.valuesService.queryParams.referralCode]);
                        subscriber.next(this.openReferralErrorModal(err));
                        subscriber.complete();

                    }
                });
            } else {
                subscriber.next(false);
                subscriber.complete();
            }
        });
    }

    automaticRedeemActivate(redeemCode, checkRedeemResponse) {
        return this.redeemService.activateRedeemCode(redeemCode)
        .pipe(
            mergeMap(resp => {
                const reason = resp?.reason;
                const response = this.redeemService.handleRedeemResponse(reason);
                const serviceId = resp?.service_id;
                if (!response.valid) {
                    return of(this.openOnboardingErrorModal(response, redeemCode));
                }

                if (serviceId && checkRedeemResponse?.data) {
                    checkRedeemResponse.data.service_id = serviceId;
                }

                const bundle = checkRedeemResponse?.data;
                return this.createFamilyGroupIfNecessaryWithBucketRefresh(bundle, 'special-cases.automaticRedeemActivate').pipe(
                    mergeMap(() => this.getRedeemActivatedModalInfo(checkRedeemResponse))
                );
            }),
            catchError(err => {
                if (err.message) {
                    const response = this.redeemService.handleRedeemResponse(err.message);
                    if (!response.valid) {
                        return of(this.openOnboardingErrorModal(response, redeemCode));
                    } else {
                        return of(false);
                    }
                } else {
                    return of(false);
                }
            })
        );
    }

    /**
     * Creates a family group when needed, then refreshes superapp bucket testing with the new group_id.
     * @private
     * @memberof SpecialCasesService
     * @param {BundleModel} bundle The activated subscription bundle
     * @returns {Observable<CreateGroupResponse|boolean>}
     */
    private createFamilyGroupIfNecessaryWithBucketRefresh(
        bundle: BundleModel,
        source: string
    ): Observable<CreateGroupResponse | boolean> {
        return this.groupManagementService.createFamilyGroupIfNecessary(bundle).pipe(
            switchMap(result => {
                const groupId = typeof result === 'object' && result?.group_id ? result.group_id : null;

                if (!groupId) {
                    return of(result);
                }

                return this.bucketTestingService.refreshSuperappEligibilityWithGroup(groupId).pipe(
                    map(() => result),
                    catchError(() => of(result))
                );
            })
        );
    }

    /**
     * Decides which modal to open after an activation code is redeemed via the automatic (deep-link) flow.
     * SuperApp-eligible users get the new confetti "subscription is active" modal; everyone else keeps
     * the classic onboarding activation flow.
     * @private
     * @memberof SpecialCasesService
     * @param {RedeemDataModel} redeemResponse The activated subscription data
     * @returns {Observable<AllModalInformation>} The modal info to open
     */
    private getRedeemActivatedModalInfo(redeemResponse: RedeemDataModel): Observable<AllModalInformation> {
        return this.bucketTestingService.checkSuperappEligibility()
        .pipe(
            map(isEligible => isEligible
                ? this.openRedeemCongratsSuperappModal(redeemResponse)
                : this.openRedeemAutomaticActivationModal(redeemResponse)),
            catchError(() => of(this.openRedeemAutomaticActivationModal(redeemResponse)))
        );
    }

    /**
     * Builds the modal info for the confetti "subscription is active" modal shown after a redeem activation.
     * @private
     * @memberof SpecialCasesService
     * @param {RedeemDataModel} redeemResponse The activated subscription data
     * @returns {AllModalInformation} The confetti modal properties
     */
    private openRedeemCongratsSuperappModal(redeemResponse: RedeemDataModel): AllModalInformation {
        return {
            name: ModalName.redeemConfirmationSuperappModal,
            containerOptions: {
                size: ModalSize.LG,
                backdropDismissable: false
            },
            contentOptions: this.buildCongratsModalContent(redeemResponse)
        } as AllModalInformation;
    }

    /**
     * Function used to compute the opening properities for onboarding modal after an activation code is checked and a new subscription is added
     * @param {RedeemDataModel} redeemResponse The response from the code check
     * @param {boolean} trial The flag that shows if the subscription is a trial
     * @returns {AllModalInformation} The object containing the modal properties
     */
    private openRedeemAutomaticActivationModal(redeemResponse: RedeemDataModel, trial?: boolean): AllModalInformation {
        const bundleId = redeemResponse?.data?.bundle_id;
        let scenario = this.valuesService.onboardingScenario.AUTOMATIC_ACTIVATION;

        if (this.valuesService.identityProducts.has(bundleId)) {
            scenario = this.valuesService.onboardingScenario.AUTOMATIC_ACTIVATION_IDENTITY;
        }

        if (bundleId === this.valuesService.bundlePM) {
            scenario = this.valuesService.onboardingScenario.AUTOMATIC_ACTIVATION_PASSMANAGER;
        }

        const modalData: AllModalInformation = {
            name: ModalName.onboardingModal,
            containerOptions: {
                size: ModalSize.LG,
                backdropDismissable: false
            },
            contentOptions: {
                redeemResponse,
                scenario
            }
        };

        if (trial) {
            modalData.contentOptions.trial = true;
        }
        return modalData;
    }

    /**
     * Function used to compute the opening properities for the autorenewal modal after an activation code is checked and it has a auto renewal flow
     * @param {RedeemDataModel} redeemResponse The response from the code check
     * @param {string} redeemCode The activation code
     * @param {ConversionStatus} status The status of the conversion
     * @returns {AllModalInformation} The object containing the modal properties
     */
    private openAutoRenewalModal(redeemResponse: RedeemDataModel, redeemCode: string, status: ConversionStatus, campaign: string): AllModalInformation {
        return {
            name: ModalName.autoRenewalModal,
            containerOptions: {
                size: ModalSize.LG,
                backdropDismissable: ConversionStatus.MANDATORY !== status,
                buttonDismissable: ConversionStatus.MANDATORY !== status
            },
            contentOptions: {
                campaign,
                redeemResponse,
                redeemCode,
                flow: ConversionStatus[status],
                scenario: this.valuesService.onboardingScenario.CONFIRM_ACTIVATION,
            }
        } as AllModalInformation
    }

    /**
     * Function used to compute the opening properities for the onboarding modal after an activation code is activated successfully
     * @param {RedeemDataModel} redeemResponse The response from the code check
     * @param {string} redeemCode The activation code
     * @returns {AllModalInformation} The object containing the modal properties
     */
    private openExtendOrActivateOnboardingModal(redeemResponse: RedeemDataModel, redeemCode: string): AllModalInformation {
        return {
            name: ModalName.onboardingModal,
            containerOptions: {
                size: ModalSize.LG
            },
            contentOptions: {
                redeemResponse,
                scenario: this.valuesService.onboardingScenario.CONFIRM_ACTIVATION,
                redeemCode
            }
        } as AllModalInformation;
    }

    /**
     * Checks if subscription is eligible for conversion and decides what modal to open based on server response
     * @private
     * @memberof SpecialCasesService
     * @param {RedeemDataModel} redeemResponse The object containing the redeem response data
     * @param {string} redeemCode The redeem code used to activate the subscription
     * @param {boolean} hasCompatibleBundles The flag that shows if the subscription has compatible bundles
     * @returns {Observable} The modal that should be opened
     */
    private getSubscriptionConversion(redeemResponse: RedeemDataModel, redeemCode: string, hasCompatibleBundles: boolean): Observable<any> {
        return this.commercialIdsMgmtService.getConversionStatus(redeemResponse?.data?.source?.partner_id, redeemResponse?.data?.commercial_id)
        .pipe(
            map((response: GetConversionResponse|ConversionStatus) => {
                const conversionStatus = (response as GetConversionResponse)?.type ?? response;
                if (conversionStatus !== ConversionStatus.OFF) {
                    return this.openAutoRenewalModal(redeemResponse, redeemCode, conversionStatus as ConversionStatus, (response as GetConversionResponse)?.checkout_flow);
                } else if (hasCompatibleBundles) {
                    return this.openExtendOrActivateOnboardingModal(redeemResponse, redeemCode);
                } else {
                    throw of(response);
                }
            }),
            catchError(error => {
                if (hasCompatibleBundles) {
                    return of(this.openExtendOrActivateOnboardingModal(redeemResponse, redeemCode));
                } else {
                    throw of(error);
                }
            })
        );
    }

    /**
     * Decides if any modal should open and what flow should be accessed (onboarding/redeemconfirmation/autorenewal)
     * @private
     * @memberof SpecialCasesService
     * @param {string} redeemCode The redeem code used to activate the subscription
     * @returns {Observable} The info about what modal should open, or not
     */
    private _processAutomaticRedeemRedirect(redeemCode: string): Observable<boolean|any> {
        return new Observable( subscriber => {
            const hasValidGroups = this.profilesService.hasValidGroups();
            let request = null;
            if (hasValidGroups) {
                request = this.redeemService.listEligibleServices(redeemCode);
            } else {
                request = this.redeemService.checkRedeemCode(redeemCode);
            }

            request
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
                next: resp => {
                    const response = this.redeemService.handleRedeemResponse(resp?.reason);
                    const checkRedeemResponse = {...resp};
                    const serviceId = resp?.service_id;
                    const bundle = resp?.data;
                    const bundleId = bundle?.bundle_id;

                    if (response?.valid && hasValidGroups) {
                        if (this.redeemService.showExtraConfirmationStepForCodeActivation(resp)) {
                            subscriber.next(this.openOnboardingModalForSwitchScenario(redeemCode, resp, QueryParamsValues.ADD_CODE));
                            subscriber.complete();
                            return;
                        }

                        if (this.profilesService.currentGroupIsFamily()
                            && this.valuesService.sharedSubscriptionFamilyPlanBundles.has(bundleId)
                            && (!bundle?.compatible_services?.length
                                || this.profilesService.getCurrentGroup().role !== GroupRoles.FAMILY_PRIMARY_ADMIN)) {
                            subscriber.next(this.openOnboardingModalForFamilyGroupExists(redeemCode, resp));
                            subscriber.complete();
                            return;
                        }
                    }

                    if (response?.valid
                        && bundleId
                        && !this.utilsCommonService.isEmptyObject(bundle)) {

                        // VSB unfinished onboarding
                        if (bundleId === this.valuesService.bundleVSB && this.subscriptionsService.hasActiveVSBServiceOnNonVSBContext()) {
                            subscriber.next(this.openRedeemAutomaticActivationModal({data: this.subscriptionsService.getActiveVSBService()}));
                            subscriber.complete();
                            return;
                        }
                        const isEligibleForConversion = this.subscriptionsService.isEligibleForOfflineToOnlineConversion(bundle);
                        const compatibleBundlesInfo = this.redeemService.getCompatibleBundleInfoByServiceId(resp);
                        const hasCompatibleBundles = compatibleBundlesInfo.compatibleServices.length > 0;
                        const hasAutoRenewalFlow = this.configService.getAutoRenewalFlow();
                        if (serviceId && checkRedeemResponse?.data) {
                            checkRedeemResponse.data.service_id = serviceId;
                        }
                        if (isEligibleForConversion && hasAutoRenewalFlow) {
                            this.getFlowForConversionEligibility(checkRedeemResponse, redeemCode, hasCompatibleBundles)
                            .pipe(takeUntil(this.onDestroy$))
                            .subscribe(_responseConversion => {
                                subscriber.next(_responseConversion);
                                subscriber.complete();
                            });
                        } else if (hasCompatibleBundles) {
                            subscriber.next(this.openExtendOrActivateOnboardingModal(checkRedeemResponse, redeemCode));
                            subscriber.complete();
                        } else {
                            this.automaticRedeemActivate(redeemCode, checkRedeemResponse)
                            .pipe(takeUntil(this.onDestroy$))
                            .subscribe({
                                next: res => {
                                    subscriber.next(res);
                                    subscriber.complete();
                                },
                                error: err => {
                                    subscriber.next(err);
                                    subscriber.complete();
                                }
                            });
                        }
                    } else {
                        let service = null;
                        if (serviceId) {
                            service = this.redeemService.getService(serviceId);
                            if (service && !this.subscriptionsService.isExpired(service)) {
                                this.bucketTestingService.getRedeemConfirmationModalInfo(
                                    this.buildCongratsModalContent({ data: service })
                                )
                                .pipe(takeUntil(this.onDestroy$))
                                .subscribe({
                                    next: modalInfo => {
                                        subscriber.next(modalInfo);
                                        subscriber.complete();
                                    }
                                });
                            } else {
                                const expiredResponse = this.redeemService.handleRedeemResponse(this.subscriptionsValuesService.redeemReasons.expired);
                                subscriber.next(this.openOnboardingErrorModal(expiredResponse, redeemCode));
                                subscriber.complete();
                            }
                        } else {
                            subscriber.next(this.openOnboardingErrorModal(response, redeemCode));
                            subscriber.complete();
                        }
                    }
                },
                error: () => {
                    subscriber.next(false);
                    subscriber.complete();
                }
            });
        });
    }

    /**
     * Computes the modal info for the switch scenario onboarding modal (you are trying to activate a redeem that is not suitable for the current context)
     * @private
     * @memberof SpecialCasesService
     * @param {string} redeemCode The activation code
     * @param {RedeemDataModel} redeemResponse The redeem data object
     * @param {QueryParamsValues} serviceAction The action to be performed for the redeem, the one that comes from the query params ex. add_code
     * @returns {AllModalInformation} The object containing the modal properties
     */
    private openOnboardingModalForSwitchScenario(redeemCode: string, redeemResponse: RedeemDataModel, serviceAction: QueryParamsValues): AllModalInformation {
        return {
            name: ModalName.onboardingModal,
            containerOptions: {
                size: ModalSize.LG,
                backdropDismissable: false
            },
            contentOptions: {
                scenario: this.valuesService.onboardingScenario.SWITCH_SCENARIO,
                redeemCode,
                redeemResponse,
                serviceAction
            }
        } as AllModalInformation;
    }

    /**
     * Function used to compute the opening properities for onboarding modal after an activation code is checked and fails
     * @param {any} response The response from the code check
     * @param {string} redeemCode The activation code
     * @param {ModalContentOptionsError} extraContentOptionsParameters The extra parameters for the modal, trialId, inviteId, groupId
     * @returns {AllModalInformation} The object containing the modal properties
     */
    private openOnboardingErrorModal(response: any, redeemCode: string, extraContentOptionsParameters?: ModalContentOptionsError): AllModalInformation {
        let scenario = this.valuesService.onboardingScenario.ERROR;
        if (extraContentOptionsParameters?.trialId) {
            scenario = this.valuesService.onboardingScenario.ERROR_TRIAL;
        } else if (extraContentOptionsParameters?.inviteId) {
            scenario = this.valuesService.onboardingScenario.ERROR_INVITE;
        } else if (extraContentOptionsParameters?.groupId) {
            scenario = this.valuesService.onboardingScenario.ERROR_JOIN_GROUP;
        }

        return {
            name: ModalName.onboardingModal,
            containerOptions: {
                size: ModalSize.LG,
                backdropDismissable: false
            },
            contentOptions: {
                redeemCode,
                errorObject: response,
                scenario,
                ...extraContentOptionsParameters
            }
        };
    }

    /**
     * Computes the modal info for the secure payment modal error
     * @returns {AllModalInformation} The object containing the modal properties
     */
    private openSecurePaymentModalError(): AllModalInformation {
        return {
            name: ModalName.securedPaymentModal,
            containerOptions: {
                size: ModalSize.MD,
                backdropDismissable: false,
                buttonDismissable: false
            } as ModalContainerOptions,
            contentOptions: {
                scenario: SecuredPaymentScenarios.ERROR
            }
        } as AllModalInformation;
    }

    /**
     * Get flow for bundle eligible for conversion, or continue to existing onboarding flow
     * @private
     * @memberof SpecialCasesService
     * @param {RedeemDataModel} redeemResponse The redeem data object
     * @param {string} redeemCode The redeem code
     * @param {bundle} hasCompatibleBundles Shows if there are compatible bundles with the new one added
     * @param {UrlProperties} urlProperties The object that contains all url properties
     * @returns {Observable} The modal that should be opened
     */
    private getFlowForConversionEligibility(redeemResponse: RedeemDataModel, redeemCode: string, hasCompatibleBundles: boolean): Observable<any> {
        return new Observable(subscriber => {
            this.getSubscriptionConversion(redeemResponse, redeemCode, hasCompatibleBundles)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
                next: resp => {
                    subscriber.next(resp);
                    subscriber.complete();
                },
                error: () => {
                    this.automaticRedeemActivate(redeemCode, redeemResponse)
                    .pipe(takeUntil(this.onDestroy$))
                    .subscribe({
                        next: res => {
                            subscriber.next(res);
                            subscriber.complete();
                        },
                        error: err => {
                            subscriber.next(err);
                            subscriber.complete();
                        }
                    });
                }
            });
        });
    }

    /**
     * @private
     * @return {*}  {(Observable<boolean|UrlTree>)}
     */
    public _decideAutomaticRedeemRedirect(): Observable<boolean|UrlTree|any> {
        return new Observable( subscriber => {
            const service = this.queryParamsService.get(this.valuesService.queryParams.service);
            const code = this.queryParamsService.get(this.valuesService.queryParams.code);

            if (this.profilesService.hasMspOrXspLevelOne() && !this.configService.getActivateRedeemRights()) {
                subscriber.next(false);
                subscriber.complete();
                return;
            }

            if (service && code && service === QueryParamsValues.ADD_CODE) {
                this._processAutomaticRedeemRedirect(code)
                .pipe(takeUntil(this.onDestroy$))
                .subscribe(res => {
                    this.queryParamsService.remove([this.valuesService.queryParams.service, this.valuesService.queryParams.code]);
                    subscriber.next(res);
                    subscriber.complete();
                });
            } else {
                subscriber.next(false);
                subscriber.complete();
            }
        });
    }

    automaticTrialActivate(trialId, subscription) {
        return this.subscriptionsTrialService.activateTrialId(trialId)
        .pipe(
            mergeMap(resp => {
                const reason = resp?.reason;
                const response = this.subscriptionsTrialService.handleTrialResponse(reason);
                if (response.valid) {
                    this.adobeDataLayerService.triggerTrialActivated(trialId);
                    const redeemData: RedeemDataModel = { data: subscription };
                    const serviceId = resp?.service_id;
                    if (serviceId && redeemData.data) {
                        redeemData.data.service_id = serviceId;
                    }
                    return this.getTrialActivatedModalInfo(redeemData);
                } else {
                    this.adobeDataLayerService.triggerTrialNotActivated(trialId);
                    return of(this.openOnboardingErrorModal(response, null, { trialId }));
                }
            }),
            catchError(err => {
                this.adobeDataLayerService.triggerTrialNotActivated(trialId);
                const response = this.subscriptionsTrialService.handleTrialResponse(err.reason);
                if (!response.valid) {
                    return of(this.openOnboardingErrorModal(response, null, { trialId }));
                } else {
                    return of(false);
                }
            })
        );
    }

    /**
     * Decides which modal to open after a trial has been activated.
     * SuperApp-eligible users get the new confetti "trial is active" modal,
     * everyone else keeps the classic onboarding activation flow.
     * @param {RedeemDataModel} redeemResponse The activated trial subscription data
     * @returns {Observable<AllModalInformation>} The modal info to open
     */
    private getTrialActivatedModalInfo(redeemResponse: RedeemDataModel): Observable<AllModalInformation> {
        return this.bucketTestingService.checkSuperappEligibility()
        .pipe(
            map(isEligible => isEligible
                ? this.openTrialCongratsSuperappModal(redeemResponse)
                : this.openRedeemAutomaticActivationModal(redeemResponse, true)),
            catchError(() => of(this.openRedeemAutomaticActivationModal(redeemResponse, true)))
        );
    }

    /**
     * Builds the modal info for the new SuperApp confetti modal shown after a trial activation
     * @param {RedeemDataModel} redeemResponse The activated trial subscription data
     * @returns {AllModalInformation} The confetti modal properties
     */
    private openTrialCongratsSuperappModal(redeemResponse: RedeemDataModel): AllModalInformation {
        return {
            name: ModalName.redeemConfirmationSuperappModal,
            containerOptions: {
                size: ModalSize.LG,
                backdropDismissable: false
            },
            contentOptions: {
                redeemResponse,
                trial: true,
                scenario: this.valuesService.onboardingScenario.TRIAL
            }
        } as AllModalInformation;
    }

    _processAutomaticTrialRedirect(trialId): Observable<boolean|AllModalInformation> {
        return new Observable( subscriber => {

            this.subscriptionsTrialService.checkTrialId(trialId)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
                next: resp => {

                    this.automaticTrialActivate(trialId, resp)
                    .pipe(takeUntil(this.onDestroy$))
                    .subscribe({
                        next: respTrialActivate => {
                            subscriber.next(respTrialActivate);
                            subscriber.complete();
                        }
                    });
                },
                error: err => {
                    const response = this.subscriptionsTrialService.handleTrialResponse(err.reason);
                    if (!response.valid) {
                        this.adobeDataLayerService.triggerTrialNotActivated(trialId);
                        subscriber.next(this.openOnboardingErrorModal(response, null, { trialId }));
                        subscriber.complete();
                    } else {
                        subscriber.next(false);
                        subscriber.complete();
                    }
                }
            });
        });
    }

    _decideAutomaticTrialRedirect(): Observable<boolean|AllModalInformation> {
        return new Observable( subscriber => {
            const service = this.queryParamsService.get(this.valuesService.queryParams.service);
            const code = this.queryParamsService.get(this.valuesService.queryParams.code);

            if (service && code && service === QueryParamsValues.ADD_TRIAL) {
                this.queryParamsService.remove([this.valuesService.queryParams.service, this.valuesService.queryParams.code]);
                const automaticTrialId = code;

                if (this.profilesService.ownerHasSubsV4()) {
                    this._processAutomaticTrialRedirect(automaticTrialId)
                    .pipe(takeUntil(this.onDestroy$))
                    .subscribe(res => {
                        subscriber.next(res);
                        subscriber.complete();
                    });
                } else {
                    subscriber.next(false);
                    subscriber.complete();
                    // Do the same redirect but to the new page.
                }
            }
            else {
                subscriber.next(false);
                subscriber.complete();
            }
        });
    }

    /**
     * Function that decides if the 'superior-trial-modal' should be opened
     * if user is eligible for both trial and better trial -> open modal
     * if user is eligible for trial and not eligible for better trial -> activate trial
     * if user is not eligible for trial and is eligible for better trial -> activate better trial
     * @param {none}
     * @returns {Observable}
     */
    public _decideSuperiorTrial(): Observable<any> {
        return new Observable( subscriber => {
            const service = this.queryParamsService.get(this.valuesService.queryParams.service);
            const trialId = this.queryParamsService.get(this.valuesService.queryParams.code);
            const betterTrialId = this.queryParamsService.get(this.valuesService.queryParams.bcode);

            if (service && trialId && betterTrialId && service === QueryParamsValues.ADD_TRIAL) {
                this.queryParamsService.remove([
                    this.valuesService.queryParams.service,
                    this.valuesService.queryParams.code,
                    this.valuesService.queryParams.bcode
                ]);

                // eligibility check
                this._processBetterTrial(trialId, betterTrialId)
                .pipe(takeUntil(this.onDestroy$))
                .subscribe((res: SuperiorTrialResponse) => {
                    if (res[SuperiorTrialKeys.TRIAL]?.status === SubscriptionStatus.ACTIVE && res[SuperiorTrialKeys.BETTER_TRIAL]?.status === 1) {
                        this._processAutomaticTrialRedirect(trialId)
                        .pipe(takeUntil(this.onDestroy$))
                        .subscribe(resp => {
                            subscriber.next(resp);
                            subscriber.complete();
                        });
                    } else if (res[SuperiorTrialKeys.TRIAL]?.status === 1 && res[SuperiorTrialKeys.BETTER_TRIAL]?.status === SubscriptionStatus.ACTIVE) {
                        this._processAutomaticTrialRedirect(betterTrialId)
                        .pipe(takeUntil(this.onDestroy$))
                        .subscribe(resp => {
                            subscriber.next(resp);
                            subscriber.complete();
                        });
                    } else if (res[SuperiorTrialKeys.TRIAL]?.status === SubscriptionStatus.ACTIVE
                            && res[SuperiorTrialKeys.BETTER_TRIAL]?.status === SubscriptionStatus.ACTIVE) {
                        subscriber.next(this.openSuperiorTrialModal(res));
                        subscriber.complete();
                    } else {
                        const response = this.subscriptionsTrialService.handleTrialResponse(res[SuperiorTrialKeys.TRIAL]?.reason);
                        if (!response.valid) {
                            subscriber.next(this.openOnboardingErrorModal(response, null, { trialId }));
                            subscriber.complete();
                        } else {
                            subscriber.next(false);
                            subscriber.complete();
                        }
                    }
                });

            } else {
                subscriber.next(false);
                subscriber.complete();
            }
        });
    }

    /**
     * Computes the modal info for the superior trial modal
     * @returns {AllModalInformation} The object containing the modal properties
     */
    private openSuperiorTrialModal(modalData: SuperiorTrialResponse): AllModalInformation {
        return {
            name: ModalName.superiorTrialModal,
            containerOptions: {
                size: ModalSize.LG
            },
            contentOptions: {
                modalData
            }
        } as AllModalInformation;
    }

    /**
     * Process superior trial modal
     *
     * @private
     * @memberof SpecialCasesService
     * @param {string, string} trialId betterTrialId representing the trial id and the better trial id
     * @returns {Observable<any>}
     */
    private _processBetterTrial(trialId, betterTrialId): Observable<any> {
        return forkJoin({
            [SuperiorTrialKeys.TRIAL]: this.subscriptionsTrialService.checkTrialId(trialId),
            [SuperiorTrialKeys.BETTER_TRIAL]: this.subscriptionsTrialService.checkTrialId(betterTrialId)
        })
        .pipe(
            map((res: SuperiorTrialResponse) => {
                res[SuperiorTrialKeys.TRIAL].trial_id = trialId;
                res[SuperiorTrialKeys.BETTER_TRIAL].trial_id = betterTrialId;
                res[SuperiorTrialKeys.TRIAL].friendly_name = this.utilsCommonService.removeFirstWord(res[SuperiorTrialKeys.TRIAL].bundle_friendly_name);
                res[SuperiorTrialKeys.BETTER_TRIAL].friendly_name = this.utilsCommonService.removeFirstWord(res[SuperiorTrialKeys.BETTER_TRIAL].bundle_friendly_name);
                return res;
            }),
            catchError(err => err)
        );
    }




    /**
     * Process automatic group join and reloads the page if the context switch is successful or shows onboarding modal with error.
     * Family groups are treated separately from the other groups.
     * @private
     * @memberof SpecialCasesService
     * @param {string} groupId Representing the group id to join
     * @returns {Observable} The observable that contains false or the response from the joining group flow
     */
     private _processAutomaticJoinGroup(groupId: string, groupType: GroupTypes, accepted?: boolean, famMigrate?: boolean): Observable<boolean|AllModalInformation> {
        if (this.profilesService.currentGroupIsVSB()) {
            return of(this.openOnboardingErrorJoinGroupSwitchModal(groupId));
        }
        return this.acknowledgeGroupInvite(groupId, groupType, accepted, famMigrate);
    }

    /**
     * Acknowledge family group invite and redo the list subscriptions request to obtain the newly added subscription
     * Open the onboarding modal for invite scenario or open the onboarding error modal
     * @private
     * @memberof SpecialCasesService
     * @param {string} groupId The group id
     * @param {GroupTypes} groupType The group type of the invitation
     * @returns {Observable} The observable that contains false or the response for openning the onboarding modal
     */
    private acknowledgeGroupInvite(groupId: string, groupType: GroupTypes, accepted?: boolean, famMigrate?: boolean): Observable<boolean|AllModalInformation> {

        return this.connectGroupMgmtService.acknowledgeInvite(groupId, accepted, famMigrate)
        .pipe(
            takeUntil(this.onDestroy$),
            mergeMap((ackResponse: Group) => this.prehandleAckInviteResponseWrapper(ackResponse, groupType)),
            map((resp: AckInviteResponse|null) => {
                const result = this.handleAckInviteResponseWrapper(resp, groupType);
                if (!result) {
                    throw resp;
                }
                return result;

            }),
            catchError(err => {
                const errorObject = err?.error?.data || err?.message?.internal_data;
                if (errorObject) {
                    const errorData = this.inviteService.handleJoinGroupInviteErrorResponse(err);
                    if (errorData) {
                        return of(this.openOnboardingErrorModal(errorData, null, { groupId }));
                    }
                }
                return of(false);
            })
        );
    }

    /**
     * Prehandles the acknowledge invite response for family and VSB groups
     * @param {AckInviteResponseWrapper} ackResponse The acknowledge invite response
     * @param {GroupTypes} groupType The group type of the invitation
     * @returns {Observable} The observable that contains the response for the acknowledge invite response prehandler
     */
    private prehandleAckInviteResponseWrapper(ackResponse: AckInviteResponse, groupType: GroupTypes): Observable<AckInviteResponseWrapper|null> {
        if (groupType === GroupTypes.FAMILY) {
            return this.prehandleAckInviteResponseForFamily(ackResponse);
        } else if (groupType === GroupTypes.VSB) {
            return this.prehandleAckInviteResponseForVSB(ackResponse);
        }
        return of(null);
    }

    /**
     * Prehandles the acknowledge invite response for family groups
     * @param {AckInviteResponseWrapper} ackResponse The acknowledge invite response
     * @returns {Observable} The observable that contains the response for the acknowledge invite response prehandler
     */
    private prehandleAckInviteResponseForFamily(ackResponse: AckInviteResponseWrapper): Observable<AckInviteResponseWrapper> {
        this.subscriptionsService.updateSubscriptions();
        return this.subscriptionsService.list()
        .pipe(
            takeUntil(this.onDestroy$),
            map(() => {
                this.subscriptionsService.processSubscriptions();
                this.subscriptionsService.populateFilteredSubscriptionsStructures();
                this.subscriptionsService.setHasZuora();
                return ackResponse;
            }),
            catchError(listError => {
                throw listError;
            })
        );
    }

    /**
     * Prehandles the acknowledge invite response for VSB groups
     * @param {AckInviteResponseWrapper} ackResponse The acknowledge invite response
     * @returns {Observable} The observable that contains the response for the acknowledge invite response prehandler
     */
    private prehandleAckInviteResponseForVSB(ackResponse: AckInviteResponseWrapper): Observable<AckInviteResponseWrapper> {
        return this.contextService.switchContext(ackResponse.context_id).pipe(
            map((switchResponse: SwitchContextResponse) => {
                return {
                    ...ackResponse,
                    ...switchResponse
                };
            })
        );
    }

    /**
     * Handles the acknowledge invite response for family and VSB groups after the prehandler
     * @param {AckInviteResponseWrapper|null} ackResponse The prehandler response
     * @param {GroupTypes} groupType The group type of the invitation
     * @returns {boolean|AllModalInformation} The response for the acknowledge invite response handler
     */
    private handleAckInviteResponseWrapper(ackResponse: AckInviteResponseWrapper|null, groupType: GroupTypes): boolean|AllModalInformation {
        if (groupType === GroupTypes.FAMILY) {
            return this.handleAckInviteResponseForFamily();
        } else if (groupType === GroupTypes.VSB) {
            return this.handleAckInviteResponseForVSB(ackResponse);
        }
        return true;
    }

    /**
     * Handles the acknowledge invite response for family groups after the prehandler
     * @returns {AllModalInformation} The response for the acknowledge invite response handler
     */
    private handleAckInviteResponseForFamily(): AllModalInformation {
        const familyPlanService = this.subscriptionsService.getActiveFamilyPlanWithGroup();
        return this.openOnboardingModalForInviteScenario(familyPlanService, null);
    }

    /**
     * Handles the acknowledge invite response for VSB groups after the prehandler
     * @param {SwitchContextResponse|null} response The prehandler response
     * @returns {boolean|AllModalInformation} The response for the acknowledge invite response handler.
     * Returns false if the context switch was not successful, true otherwise. For false, an error will be thrown at the next level.
     */
    private handleAckInviteResponseForVSB(response: AckInviteResponseWrapper|null): boolean {
        if (response.switched) {
            localStorage.setItem(ContextBubbleLocalStorage, BrowserBooleanValues.TRUE);
            window.location.href = window.location.href.split('?')[0];
            return true;
        }
        return false;
    }

    public _decideOnboardingVSB(): Observable<boolean|AllModalInformation> {
        return new Observable( subscriber => {
            if (!this.settingsService.isOnboardingComplete()
                && this.profilesService.getCurrentGroupType() === GroupTypes.VSB) {
                const vsbService = this.subscriptionsService.getActiveVSBService();
                if (vsbService) {
                    localStorage.setItem(ContextBubbleLocalStorage, BrowserBooleanValues.TRUE);
                    this.getVSBOnboardingModalInfo(vsbService)
                    .pipe(takeUntil(this.onDestroy$))
                    .subscribe(modalInfo => {
                        subscriber.next(modalInfo);
                        subscriber.complete();
                    });
                    return;
                }
            }
            subscriber.next(false);
            subscriber.complete();
        });

    }

    /**
     * Decides which modal to open for a VSB member completing onboarding.
     * SuperApp-eligible members get the new confetti "subscription is active" modal (using the
     * company name); everyone else keeps the classic onboarding "Included in your subscription plan" flow.
     * @private
     * @memberof SpecialCasesService
     * @param {BundleModel} vsbService The active VSB subscription
     * @returns {Observable<AllModalInformation>} The modal info to open
     */
    private getVSBOnboardingModalInfo(vsbService: BundleModel): Observable<AllModalInformation> {
        return this.bucketTestingService.checkSuperappEligibility()
        .pipe(
            map(isEligible => {
                if (isEligible) {
                    // The confetti modal doesn't run the onboarding-modal lifecycle, so mark onboarding
                    // complete here; otherwise it would re-open on every navigation
                    this.markOnboardingComplete();
                    return this.openVSBActivationCongratsSuperappModal(vsbService);
                }
                return this.openOnboardingModalForInviteScenario(vsbService, null);
            }),
            catchError(() => of(this.openOnboardingModalForInviteScenario(vsbService, null)))
        );
    }

    /**
     * Marks the account onboarding as complete (best effort).
     * The classic onboarding modal does this in its lifecycle; the confetti modal does not, so callers
     * that open the confetti in place of the onboarding modal must mark it explicitly.
     * @private
     * @memberof SpecialCasesService
     */
    private markOnboardingComplete(): void {
        this.settingsService.setOnboardingComplete()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({ error: () => {} });
    }

    /**
     * Builds the modal info for the confetti "subscription is active" modal shown to VSB members.
     * The heading uses the current group's company name.
     * @private
     * @memberof SpecialCasesService
     * @param {BundleModel} vsbService The active VSB subscription
     * @returns {AllModalInformation} The confetti modal properties
     */
    private openVSBActivationCongratsSuperappModal(vsbService: BundleModel): AllModalInformation {
        return {
            name: ModalName.redeemConfirmationSuperappModal,
            containerOptions: {
                size: ModalSize.LG,
                backdropDismissable: false
            },
            contentOptions: {
                redeemResponse: { data: vsbService },
                companyName: this.profilesService.getCurrentGroupLabel(),
                scenario: this.valuesService.onboardingScenario.CONGRATULATIONS_ACTIVATE
            }
        } as AllModalInformation;
    }

    /**
     * Decides if the onboarding modal should be opened for the unfinished family onboarding
     * @public
     * @memberof SpecialCasesService
     * @returns {Observable} The observable that contains false or the response from the onboarding modal
     */
    public _decideUnfinishedFamilyOnboarding(): Observable<boolean|AllModalInformation> {
        return new Observable(subscriber => {
            const storageHasFamilyGroupError = sessionStorage.getItem(this.valuesService.sessionStorageModalStatus.hasHasFamilyGroupError);
            const familyPlanServiceId = localStorage.getItem(FamilyPlanService);

            if (familyPlanServiceId
                && !this.modalRoutelessService.getIsModalOpened()
                && (this.settingsService.getHasFamilyGroupError() || storageHasFamilyGroupError)) {
                const familyPlanService = this.subscriptionsService.getBundleByServiceId(familyPlanServiceId);
                if (familyPlanService) {
                    localStorage.removeItem(FamilyPlanService);
                    subscriber.next(this.openRedeemAutomaticActivationModal({data: familyPlanService}));
                    subscriber.complete();
                }
            }
            subscriber.next(false);
            subscriber.complete();
        });
    }

    public _decideOpenInstallModalToSetupVSB(): Observable<boolean|AllModalInformation> {
        return new Observable( subscriber => {
            const newContextId = localStorage.getItem(OpenInstallModal);
            if (this.profilesService.getCurrentGroupType() === GroupTypes.VSB
                && this.profilesService.getCurrentContextId() === newContextId) {
                const vsbService = this.subscriptionsService.getActiveVSBService();
                if (vsbService) {
                    localStorage.removeItem(OpenInstallModal);
                    subscriber.next(this.openInstallModalForBundle(vsbService));
                    subscriber.complete();
                }
            }
            subscriber.next(false);
            subscriber.complete();
        });
    }

    /**
     * Function that computes the modal info for the install modal, and opens it for the given bundle, the default step (first one)
     * @param {BundleModel} bundle The bundle object
     * @returns {AllModalInformation} The object containing the modal properties
     */
    private openInstallModalForBundle(bundle: BundleModel): AllModalInformation {
        const contentOptions = {
            bundle_id : bundle?.bundle_id,
            newBundle: bundle
        };
        this.bucketTestingService.openInstallModal({size: ModalSize.LG}, contentOptions, true);

        const _response: AllModalInformation = {
            name: ModalName.installModal,
            containerOptions: {
                size: ModalSize.LG
            },
            contentOptions: {
                bundle_id : bundle?.bundle_id,
                newBundle: bundle
            }
        };
        return _response;
    }

    /**
     * Computes the modal info for the install modal, the default step (first one)
     * @returns {AllModalInformation} The object containing the modal properties
     */
    private openInstallModal(): AllModalInformation {
        return {
            name: ModalName.installModal,
            containerOptions: {
                size: ModalSize.LG
            },
            contentOptions: {}
        } as AllModalInformation;
    }

    /**
     * Computes the modal info for the onboarding modal to be opened for the invite scenario
     * @private
     * @memberof SpecialCasesService
     * @param {BundleModel} bundle The bundle object
     * @param {string} inviteId The invite id
     * @returns {AllModalInformation} The object containing the modal properties
     */
    private openOnboardingModalForInviteScenario(bundle: BundleModel, inviteId: string): AllModalInformation {
        return {
            name: ModalName.onboardingModal,
            containerOptions: {
                size: ModalSize.LG,
                backdropDismissable: false
            },
            contentOptions: {
                scenario: this.valuesService.onboardingScenario.AUTOMATIC_ACTIVATION_INVITE,
                bundleId: bundle?.bundle_id,
                bundleName: bundle?.bundle_friendly_name,
                inviteId
            }
        } as AllModalInformation;
    }

    /**
     * Process automatic invite redirect and open onboarding modal for corresponding scenario
     *
     * @private
     * @memberof SpecialCasesService
     * @param {String} inviteId representing the invite link sent on email
     * @returns {Observable}
     */
    private _processAutomaticInviteRedirect(inviteId: string): Observable<boolean|AllModalInformation> {     
        return new Observable( subscriber => {
            this.subscriptionInviteService.activateInvite(inviteId)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
                next: resp => {
                    subscriber.next(this.openOnboardingModalForInviteScenario(resp, inviteId));
                    subscriber.complete();
                },
                error: err => {
                    const errorObject = err?.error?.data || err?.message?.internal_data;
                    if (errorObject) {
                        const errorData = this.inviteService.handleInviteServiceErrorResponse(errorObject);
                        if (errorData) {        
                            subscriber.next(this.openOnboardingErrorModal(errorData, null, { inviteId }));
                            subscriber.complete();
                        } else {
                            subscriber.next(false);
                            subscriber.complete();
                        }
                    } else {
                        subscriber.next(false);
                        subscriber.complete();
                    }
                }
            });
        });
    }

    /**
     * If user is on 'add_invite' scenario process automatic invite redirect
     *
     * @public
     * @memberof SpecialCasesService
     * @returns {Observable}
     */
    public _decideAutomaticInviteRedirect(): Observable<boolean|AllModalInformation> {
        return new Observable( subscriber => {
            const service = this.queryParamsService.get(this.valuesService.queryParams.service);
            const inviteId = this.queryParamsService.get(this.valuesService.queryParams.inviteId);

            if (this.profilesService.hasMspOrXspLevelOne()) {
                subscriber.next(false);
                subscriber.complete();
                return;
            }

            if (service && inviteId && service === QueryParamsValues.ADD_INVITE) {
                this.queryParamsService.remove([this.valuesService.queryParams.service, this.valuesService.queryParams.inviteId]);
                if (this.profilesService.currentGroupIsVSB()) {
                    subscriber.next(this.openOnboardingErrorInviteSwitchModal(inviteId));
                    subscriber.complete();
                } else if (this.profilesService.ownerHasSubsV4()) {
                    this._processAutomaticInviteRedirect(inviteId)
                    .pipe(takeUntil(this.onDestroy$))
                    .subscribe(res => {
                        subscriber.next(res);
                        subscriber.complete();
                    });
                } else {
                    subscriber.next(false);
                    subscriber.complete();
                }
            } else {
                subscriber.next(false);
                subscriber.complete();
            }
        });
    }

    /**
     * Function that computes the modal info for the onboarding modal to be opened for the error invite switch scenario
     * @param {string} inviteId The invite id
     * @returns {AllModalInformation} The object containing the modal properties
     */
    private openOnboardingErrorInviteSwitchModal(inviteId: string): AllModalInformation {
        return {
            name: ModalName.onboardingModal,
            containerOptions: {
                size: ModalSize.LG,
                backdropDismissable: false
            },
            contentOptions: {
                scenario: this.valuesService.onboardingScenario.ERROR_INVITE_SWITCH,
                inviteId,
            }
        } as AllModalInformation;
    }

    /**
     * Computes the modal info for the onboarding modal to be opened for the error join group switch scenario
     * @param {string} groupId The group id
     * @returns {AllModalInformation} The modal properties object
     */
    private openOnboardingErrorJoinGroupSwitchModal(groupId: string): AllModalInformation {
        return {
            name: ModalName.onboardingModal,
            containerOptions: {
                size: ModalSize.LG,
                backdropDismissable: false
            },
            contentOptions: {
                scenario: this.valuesService.onboardingScenario.ERROR_JOIN_GROUP_SWITCH,
                groupId
            }
        } as AllModalInformation;
    }

    /**
     * Function that computes data for the onboarding modal to be opened
     * @returns {AllModalInformation} The object containing the modal properties
     */
    private openOnboardingModal(): AllModalInformation {
        return {
            name: ModalName.onboardingModal,
            containerOptions: {
                size: ModalSize.LG,
                backdropDismissable: false
            },
            contentOptions: {
                scenario: this.valuesService.onboardingScenario.ACTIVATION
            }
        } as AllModalInformation;
    }

    _decideOnboarding(): Observable<AllModalInformation|boolean> {
        return new Observable( subscriber => {
            if (this.profilesService.hasMspOrXspLevelOne()) {
                subscriber.next(false);
                subscriber.complete();
                return;
            }

            // Sourcenext does not support the onboarding flow (subscriptions: false)
            if (!this.configService.getSubscriptions()) {
                subscriber.next(false);
                subscriber.complete();
                return;
            }

            const _storageOnboardingComplete = sessionStorage.getItem(this.valuesService.sessionStorageModalStatus.onboardingComplete);
            if (!this.subscriptionsService.getAllServices().length && !this.settingsService.isOnboardingComplete() && !_storageOnboardingComplete) {
                subscriber.next(this.openOnboardingModal());
                subscriber.complete();
            } else {
                subscriber.next(false);
                subscriber.complete();
            }
        });
    }

    /**
     * Function that computes data for the install modal to be opened for the campaign
     * @param {string} cidCode The campaign cid code
     * @returns {AllModalInformation} The object containing the modal properties
     */
    private openOldInstallModalForCampaign(cidCode: string): AllModalInformation {
        return {
            name: ModalName.installProtectionModal,
            containerOptions: {
                size: ModalSize.LG
            },
            contentOptions: {
                scenario: ProductsToInstall.PROTECTION,
                cidCode
            }
        } as AllModalInformation;
    }

    _decideInstallRedirectFromQueryParams(): Observable<boolean|AllModalInformation> {
        return new Observable( subscriber => {
            const modal = this.queryParamsService.get(this.valuesService.queryParams.modal);
            const hasDeployFlow = this.appsConfigService.showDeployFlow(this.valuesService.appProtection);

            if (modal && modal === QueryParamsValues.INSTALL && hasDeployFlow) {
                const cid = this.queryParamsService.get(this.valuesService.queryParams.campaign);
                subscriber.next(this.openOldInstallModalForCampaign(cid));
                subscriber.complete();
                this.queryParamsService.remove([this.valuesService.queryParams.campaign]);
                this.queryParamsService.remove([this.valuesService.queryParams.modal]);
            } else {
                subscriber.next(false);
                subscriber.complete();
            }
        });
    }

    /**
     * Function that decides if the ATO feedback modal should be opened via URL parameter
     * URL format: ?modal=ato_feedback
     * @returns {Observable<boolean|AllModalInformation>} The observable that contains false or the modal info
     */
    public _decideAtoFeedbackModal(): Observable<boolean|AllModalInformation> {
        return new Observable(subscriber => {
            const modal = this.queryParamsService.get(this.valuesService.queryParams.modal);

            if (modal && modal === QueryParamsValues.ATO_FEEDBACK) {
                this.queryParamsService.remove([this.valuesService.queryParams.modal]);
                subscriber.next(this.openAtoFeedbackModal());
                subscriber.complete();
            } else {
                subscriber.next(false);
                subscriber.complete();
            }
        });
    }

    /**
     * Computes the modal info for the ATO feedback modal (full scenario)
     * @returns {AllModalInformation} The object containing the modal properties
     */
    private openAtoFeedbackModal(): AllModalInformation {
        return {
            name: ModalName.atoFeedbackModal,
            containerOptions: {
                size: ModalSize.MD
            },
            contentOptions: {
                scenario: FeedbackModalScenarios.FULL
            }
        } as AllModalInformation;
    }

    /**
     * Function that computes data for the onboarding modal to be opened for the expired subscription
     * @param {boolean} paid Ture if the subscription is paid
     * @param {boolean} protection True if the subscription has protection
     * @returns {AllModalInformation} The object containing the modal properties
     */
    private openOnboardingModalForExpiredSubscription(paid: boolean, protection: boolean): AllModalInformation {
        return {
            name: ModalName.onboardingModal,
            containerOptions: {
                size: ModalSize.LG,
                backdropDismissable: false
            },
            contentOptions: {
                scenario: this.valuesService.onboardingScenario.EXPIRED,
                paid,
                protection
            }
        } as AllModalInformation;
    }

    _decideRedirectFromExpiredSubscription(urlProperties: UrlProperties): Observable<AllModalInformation|boolean> {
        return new Observable( subscriber => {
            if (urlProperties.entirePath.indexOf(this.valuesService.centralPaths.privacy.id) !== -1) {
                subscriber.next(false);
                subscriber.complete();
                return;
            }

            if (this.profilesService.hasMspOrXspLevelOne() || !this.configService.getBuyRights()) {
                subscriber.next(false);
                subscriber.complete();
                return;
            }

            const _storageModalStatus = sessionStorage.getItem(this.valuesService.sessionStorageModalStatus.expiredOnboardingModal);
            if (this.subscriptionsService.hasAllBundlesAreExpired() && !this.modalRoutelessService.getIsModalOpened() && !_storageModalStatus) {
                if (this.subscriptionsService.hasPaidProtectionBundleExpired()) {
                    sessionStorage.setItem(this.valuesService.sessionStorageModalStatus.expiredOnboardingModal, 'true');
                    subscriber.next(this.openOnboardingModalForExpiredSubscription(true, true));
                    subscriber.complete();
                    return;
                }

                if (this.subscriptionsService.hasPaidNonprotectionBundleExpired()) {
                    sessionStorage.setItem(this.valuesService.sessionStorageModalStatus.expiredOnboardingModal, 'true');
                    subscriber.next(this.openOnboardingModalForExpiredSubscription(true, false));
                    subscriber.complete();
                    return;
                }

                if (this.subscriptionsService.hasFreeProtectionBundleExpired()) {
                    sessionStorage.setItem(this.valuesService.sessionStorageModalStatus.expiredOnboardingModal, 'true');
                    subscriber.next(this.openOnboardingModalForExpiredSubscription(false, true));
                    subscriber.complete();
                    return;
                }

                if (this.subscriptionsService.hasFreeNonprotectionBundleExpired()) {
                    sessionStorage.setItem(this.valuesService.sessionStorageModalStatus.expiredOnboardingModal, 'true');
                    subscriber.next(this.openOnboardingModalForExpiredSubscription(false, false));
                    subscriber.complete();
                    return;
                }
                subscriber.next(false);
                subscriber.complete();
            }
            subscriber.next(false);
            subscriber.complete();
        });
    }

    /**
     * Function that decides if the 'what-is-new-modal' should be opened
     * @param {none}
     * @returns {Observable}
     */
    public _decideWhatIsNew(): Observable<boolean|AllModalInformation> {
        return new Observable( subscriber => {
            const modal = this.queryParamsService.get(this.valuesService.queryParams.modal);
            const oldBundle = this.queryParamsService.get(this.valuesService.queryParams.oldBundle);
            const newBundle = this.queryParamsService.get(this.valuesService.queryParams.newBundle);

            const mandatoryParams = {};
            const optionalParams = {};

            if (modal === QueryParamsValues.SUBSCRIPTION_UPGRADE
                && this.valuesService.whatIsNewModal[oldBundle]?.[newBundle]) {

                const modalInfo = this.valuesService.whatIsNewModal[oldBundle]?.[newBundle];
                if (modalInfo.mandatoryParams) {
                    for (const param of modalInfo.mandatoryParams) {
                        mandatoryParams[param] = this.queryParamsService.get(param);
                        if (!mandatoryParams[param]) {
                            subscriber.next(false);
                            subscriber.complete();
                            return;
                        }
                    }
                }
                if (modalInfo.optionalParams) {
                    for (const param of modalInfo.optionalParams) {
                        optionalParams[param] = this.queryParamsService.get(param);
                    }
                }

                const modalOptions = {
                    name: ModalName.whatIsNewModal,
                    containerOptions: {
                        size: ModalSize.LG
                    },
                    contentOptions: {
                        oldBundle,
                        newBundle,
                        ...mandatoryParams,
                        ...optionalParams
                    }
                } as AllModalInformation;

                subscriber.next(modalOptions);
                subscriber.complete();

                this.queryParamsService.remove([
                    this.valuesService.queryParams.modal,
                    this.valuesService.queryParams.oldBundle,
                    this.valuesService.queryParams.newBundle,
                    this.valuesService.queryParams.extraDevices,
                    this.valuesService.queryParams.devices,
                    this.valuesService.queryParams.oldPlan,
                    this.valuesService.queryParams.newPlan
                ]);
            } else {
                subscriber.next(false);
                subscriber.complete();
            }
        });
    }

    _decideCongrats(): Observable<boolean|AllModalInformation|null> {
        return new Observable( subscriber => {
            const newDevicesIds = this.redeemService.getNewDevicesIds();
            if (!newDevicesIds.size) {
                subscriber.next(false);
                subscriber.complete();
                return;
            }

            for (const deviceId of newDevicesIds) {
                const device = this.devicesService.retrieveDeviceById(deviceId);
                const validProtectionApp = device?.processed?.protectionApp ?? [];
                const hasVPN = !!device?.processed?.hasVPN;
                const hasParental = !!device?.processed?.hasParental;
                const hasParentalNCC = !!device?.processed?.hasParentalNCC;
                // verificam daca are vreun app instalat (protection sau vpn)
                if ((validProtectionApp && this.valuesService.protectionApps.has(validProtectionApp))
                    || hasVPN || hasParental || hasParentalNCC) {
                    this.redeemService.removeNewDeviceId(deviceId);

                    setTimeout(() => {
                        this.messageService.sendMessage(this.valuesService.events.openCongratsNewDevice, {deviceId: deviceId});
                    }, 350);

                    const contentOptions = this.buildCongratsModalForParentalContent(device);
                    if (!contentOptions) {
                        continue;
                    }

                    this.bucketTestingService.getRedeemConfirmationModalInfo(contentOptions)
                    .pipe(takeUntil(this.onDestroy$))
                    .subscribe({
                        next: modalInfo => {
                            subscriber.next(modalInfo);
                            subscriber.complete();
                        }
                    });
                    return;
                }
            }

            subscriber.next(false);
            subscriber.complete();
        });
    }

    /**
     * Function that decides if the onboarding modal should be opened for the unfinished VSB onboarding
     * @public
     * @memberof SpecialCasesService
     * @returns {Observable<boolean|any>}
     */
    public _decideUnfinishedVSBOnboarding(): Observable<boolean|AllModalInformation> {
        return new Observable( subscriber => {
            if (this.subscriptionsService.hasActiveVSBServiceOnNonVSBContext() && !this.modalRoutelessService.getIsModalOpened()) {
                subscriber.next(this.openRedeemAutomaticActivationModal({data: this.subscriptionsService.getActiveVSBService()}));
                subscriber.complete();
            } else {
                subscriber.next(false);
                subscriber.complete();
            }
        });
    }

    /**
     * Function that decides what scenario to be displayed for 3dsecure redirects
     * @public
     * @memberof SpecialCasesService
     * @returns {Observable} Specifies which modal to open if 'paymentStatus' param is present
     */
    public _decideSecuredPayment(): Observable<AllModalInformation|boolean> {
        return new Observable(subscriber => {
            const paymentStatus = this.queryParamsService.get(this.valuesService.queryParams.paymentStatus);

            if (!paymentStatus) {
                subscriber.next(false);
                subscriber.complete();
                return;
            }

            // If success, open redeem confirmation modal
            // If error, open secured payment modal
            if (paymentStatus === SecuredPaymentScenarios.SUCCESS) {
                this.bucketTestingService.getRedeemConfirmationModalInfo(
                    this.buildCongratsModalForSecurePaymentContent()
                )
                .pipe(takeUntil(this.onDestroy$))
                .subscribe({
                    next: modalInfo => {
                        subscriber.next(modalInfo);
                        subscriber.complete();
                    }
                });
            } else {
                subscriber.next(this.openSecurePaymentModalError());
                subscriber.complete();
            }
            this.queryParamsService.remove([this.valuesService.queryParams.paymentStatus]);
        });
    }

    /**
     * Function that decides what scenario to be displayed for 3dsecure redirects for zuora
     * @public
     * @memberof SpecialCasesService
     * @returns {Observable} Specifies which modal to open
     */
    public _decide3dSecureRedirect(): Observable<boolean|AllModalInformation> {
        return new Observable(subscriber => {
            const centralFlow = this.queryParamsService.get(this.valuesService.queryParams.centralFlow);
            const bitcheckoutsdkFlow = this.queryParamsService.get(this.valuesService.queryParams.bitcheckoutsdkFlow);
            const paymentMethod = this.queryParamsService.get(this.valuesService.queryParams.paymentMethod);
            const paymentSucceeded = this.queryParamsService.get(this.valuesService.queryParams.paymentSucceeded);

            if (!centralFlow && !bitcheckoutsdkFlow && !paymentMethod && !paymentSucceeded) {
                subscriber.next(false);
                subscriber.complete();
                return;
            }

            if (bitcheckoutsdkFlow === CheckoutSdkFlows.updatePaymentMethod) {
                this.queryParamsService.remove([
                    this.valuesService.queryParams.bitcheckoutsdkFlow,
                    this.valuesService.queryParams.paymentMethod,
                    this.valuesService.queryParams.bitcheckoutsdkPaymentMethodType,
                    this.valuesService.queryParams.flowProvider
                ]);
                subscriber.next(false);
                subscriber.complete();
                return;
            }

            // If paymentSucceeded=true, open redeem confirmation modal
            // If paymentSucceeded=false, open auto renewal modal on billing step
            // If error, open secured payment modal
            if (centralFlow === this.subscriptionsValuesService.sourceType.offline && bitcheckoutsdkFlow === QueryParamsValues.PAYMENT
                && paymentSucceeded === QueryParamsValues.FALSE) {
                subscriber.next(this.openAutoRenewalResumePaymentModal());
                subscriber.complete();
            } else if (centralFlow === this.subscriptionsValuesService.sourceType.offline && paymentSucceeded === QueryParamsValues.TRUE) {
                this.bucketTestingService.getRedeemConfirmationModalInfo(
                    this.buildCongratsModalForSecurePaymentContent()
                )
                .pipe(takeUntil(this.onDestroy$))
                .subscribe({
                    next: modalInfo => {
                        subscriber.next(modalInfo);
                        subscriber.complete();
                    }
                });
            } else {
                subscriber.next(this.openSecurePaymentModalError());
                subscriber.complete();
            }
            this.queryParamsService.remove([
                this.valuesService.queryParams.centralFlow,
                this.valuesService.queryParams.bitcheckoutsdkFlow,
                this.valuesService.queryParams.paymentMethod,
                this.valuesService.queryParams.centralFlow,
                this.valuesService.queryParams.paymentSucceeded
            ]);
        });
    }

    /**
     * Function used to compute the opening properities for the autorenewal modal after an user cancels in the 3d secure step
     * @returns {AllModalInformation} The object containing the modal properties
     */
    private openAutoRenewalResumePaymentModal(): AllModalInformation {
        return {
            name: ModalName.autoRenewalModal,
            containerOptions: {
                size: ModalSize.LG,
                // default to false for scenario RESUME_PAYMENT
                backdropDismissable: false,
                buttonDismissable: false
            },
            contentOptions: {
                // neep to update in modal after getCartData()
                flow: ConversionStatus.MANDATORY,
                scenario: this.valuesService.onboardingScenario.RESUME_PAYMENT,
            }
        } as AllModalInformation;
    }

    private buildCongratsModalContent(redeemResponse: RedeemDataModel) {
        return {
            redeemResponse,
            alreadyActive: true,
            scenario: this.valuesService.onboardingScenario.CONGRATULATIONS_ACTIVATE
        };
    }

    /**
     * Computes content options for the redeem confirmation modal for installing parental ncc on a device
     * @param device The device object
     * @returns Content options, or null when the modal should not be shown
     */
    private buildCongratsModalForParentalContent(device) {
        const hasParental = !!device?.processed?.hasParental;
        const hasParentalNCC = !!device?.processed?.hasParentalNCC;
        let scenario = this.valuesService.onboardingScenario.CONGRATULATIONS;

        const aditionalParams: any = {};

        if (device?.profile_id) {
            const profileHash = this.profilesService.getProfileById(device?.profile_id)?.profile_hash;
            if (profileHash) {
                aditionalParams.profileHash = profileHash;
            }
        }

        if (hasParentalNCC && aditionalParams?.profileHash) {
            scenario = this.valuesService.onboardingScenario.PARENTALNCC;
        }

        if (hasParental && aditionalParams?.profileHash) {
            scenario = this.valuesService.onboardingScenario.PARENTAL;
        }
        //Daca nu are profile_id dar are parental nu afisam modala de congrats
        if (!device?.profile_id && (hasParental || hasParentalNCC)) {
            return null;
        }

        return {
            deviceId: device?.device_id,
            scenario,
            ...aditionalParams
        };
    }

    private buildCongratsModalForSecurePaymentContent() {
        return {
            scenario: this.valuesService.onboardingScenario.SECURED_PAYMENT
        };
    }

    /**
     * Computes the modal info for the onboarding modal for the scenario when the user has no msp or xsp, wants to activate a code and has subs v3
     * @returns {AllModalInformation} The object containing the modal properties
     */
    private openOnboardingModalForSubsV3NoMspXsp(): AllModalInformation {
        return {
            name: ModalName.onboardingModal,
            containerOptions: {
                size: ModalSize.LG,
                backdropDismissable: false
            },
            contentOptions: {
                scenario: this.valuesService.onboardingScenario.JUST_ACTIVATION
            }
        } as AllModalInformation;
    }

    /**
     *
     * Open redeem modal for accounts without msp
     * @return {*}  {(Observable<boolean|any>)}
     * @memberof SpecialCasesService
     */
    public _decideRedeemRedirectFromQueryParams(): Observable<boolean|AllModalInformation> {
        return new Observable( subscriber => {
            const modal = this.queryParamsService.get(this.valuesService.queryParams.modal);
            const hasMspOrXsp = this.profilesService.hasMspOrXspAnyLevel();

            if (modal && modal === QueryParamsValues.REDEEEM && !hasMspOrXsp) {
                subscriber.next(this.openOnboardingModalForSubsV3NoMspXsp());
                subscriber.complete();
                this.queryParamsService.remove([this.valuesService.queryParams.modal]);
            } else {
                subscriber.next(false);
                subscriber.complete();
            }
        });
    }

    /**
     * Computes the modal info for family group exists scenario
     * @private
     * @memberof SpecialCasesService
     * @param {string} redeemCode The activation code
     * @param {RedeemDataModel} redeemResponse The redeem data object
     * @returns {AllModalInformation} The object containing the modal properties
     */
    private openOnboardingModalForFamilyGroupExists(redeemCode: string, redeemResponse: RedeemDataModel): AllModalInformation {
        return {
            name: ModalName.onboardingModal,
            containerOptions: {
                size: ModalSize.LG,
                backdropDismissable: false
            },
            contentOptions: {
                scenario: this.valuesService.onboardingScenario.FAMILY_GROUP_EXISTS,
                redeemCode,
                redeemResponse
            }
        } as AllModalInformation;
    }

    /**
     * Function that decides if the payment-details-modal should be opened
     * @public
     * @memberof SpecialCasesService
     * @returns {Observable}
     */
    public _decidePaymentDetails(): Observable<boolean|AllModalInformation> {
        return new Observable(subscriber => {
            const modal = this.queryParamsService.get(this.valuesService.queryParams.modal);

            if (modal !== QueryParamsValues.UPDATE_PAYMENT) {
                subscriber.next(false);
                subscriber.complete();
            }

            const billingDetails = this.checkoutSDKService.getBillingDetails();
            if (billingDetails) {
                subscriber.next(this.openPaymentDetailsModal());
                subscriber.complete();
            } else {
                this.checkoutSDKService.getAccountBillingDetails()
                .pipe(take(1))
                .subscribe({
                    next: () => {
                        subscriber.next(this.openPaymentDetailsModal());
                        subscriber.complete();
                    }
                });
            }

            this.queryParamsService.remove([this.valuesService.queryParams.modal]);
        });
    }

     /**
     * Computes the modal info - payment update modal /  billing details modal
     * @public
     * @memberof SpecialCasesService
     * @returns {AllModalInformation} The object containing the modal properties
     */
    public openPaymentDetailsModal(paymentType = this.subscriptionsValuesService.paymentType.card): AllModalInformation {
        const modalInfo = {
            name: ModalName.paymentDetailsModal,
            containerOptions: {
                size: ModalSize.MD
            } as ModalContainerOptions,
            contentOptions: {
                paymentType
            }
        } as AllModalInformation;

        const billingDetails = this.checkoutSDKService.getBillingDetails();
        if (Object.keys(billingDetails).length && this.iSubscriptionService.checkBillingDetailsRequiredFields(billingDetails)) {
            modalInfo.name = ModalName.billingDetailsModal;
            modalInfo.contentOptions.scenario = this.subscriptionsValuesService.paymentScenarios.updateAccountBillingDetails;
        }

        return modalInfo;
    }
    /**
     * Function that decides if the migration to family plan flow should be triggered
     * @returns {Observable<boolean|AllModalInformation>} The observable that contains false or the response from the migration flow
     */
    public _decideAutomaticFamilyMigration(): Observable<boolean|AllModalInformation> {
        const service = this.queryParamsService.get(this.valuesService.queryParams.service);
        const serviceId = this.queryParamsService.get(this.valuesService.queryParams.serviceId);
        
        if (service && serviceId && service === this.valuesService.onboardingScenario.MIGRATE_TO_FAMILY) {
            this.queryParamsService.remove([
                this.valuesService.queryParams.service,
                this.valuesService.queryParams.serviceId
            ]);

            const baseModalInfo = {
                name: ModalName.onboardingModal,
                containerOptions: {
                    size: ModalSize.LG,
                    backdropDismissable: false
                },
                contentOptions: {
                    scenario: this.valuesService.onboardingScenario.MIGRATE_TO_FAMILY,
                    role: GroupRoles.FAMILY_PRIMARY_ADMIN
                }
            } as AllModalInformation;

            return this.connectGroupMgmtService.migrateToGroup(serviceId).pipe(
                map(resp => {
                    if (resp && resp.status === 0) {
                        baseModalInfo.contentOptions.accepted = true;
                        return baseModalInfo;
                    }
                    throw resp;
                }),
                catchError(err => {
                    const errorObject = err?.error?.data || err?.message?.internal_data;
                    let errorCode = '';
                    if (errorObject) {
                        errorCode = errorObject.code || errorObject.internal_code || '';
                    }
                    baseModalInfo.contentOptions.migrationError = true;
                    baseModalInfo.contentOptions.accepted = false;
                    baseModalInfo.contentOptions.errorCode = errorCode;
                    return of(baseModalInfo);
                })
            );
        }
        return of(false);
    }

    /**
     * Function that decides if the joining group flow should be triggered
     * @returns {Observable<boolean|AllModalInformation>} The observable that contains false or the response from the joining group flow
     */
    public _decideAutomaticJoinGroup(): Observable<boolean|AllModalInformation> {
        const service = this.queryParamsService.get(this.valuesService.queryParams.service);
        const groupId = this.queryParamsService.get(this.valuesService.queryParams.groupId);
        const groupType = this.queryParamsService.get(this.valuesService.queryParams.groupType);
        const flow = this.queryParamsService.get(this.valuesService.queryParams.flow);
        if (
            service && groupId &&
            (service === QueryParamsValues.JOIN_GROUP || service === QueryParamsValues.REJECT_GROUP) &&
            flow === this.valuesService.onboardingScenario.FAMILY_MIGRATION
        ) {
            this.queryParamsService.remove([
                this.valuesService.queryParams.service,
                this.valuesService.queryParams.groupId,
                this.valuesService.queryParams.flow,
                this.valuesService.queryParams.groupType
            ]);

            if (this.profilesService.ownerHasSubsV4()) {
                const accepted = service === QueryParamsValues.JOIN_GROUP;
                const famMigrate = flow === this.valuesService.onboardingScenario.FAMILY_MIGRATION
                const baseModalInfo = {
                    name: ModalName.onboardingModal,
                    containerOptions: {
                        size: ModalSize.LG,
                        backdropDismissable: false
                    },
                    contentOptions: {
                        scenario: this.valuesService.onboardingScenario.MIGRATE_TO_FAMILY,
                        role: GroupRoles.FAMILY_INDEPENDENT
                    }
                } as AllModalInformation;

                return this.connectGroupMgmtService.acknowledgeInvite(groupId, accepted, famMigrate).pipe(
                    map(resp => {
                        baseModalInfo.contentOptions.accepted = accepted;
                        baseModalInfo.contentOptions.migrationError = false
                        return baseModalInfo
                    }),
                    catchError(err => {
                        const errorObject = err?.error?.data || err?.message?.internal_data;
                        let errorCode = '';
                        if (errorObject) {
                            errorCode = errorObject.code || errorObject.internal_code || '';
                        }
                        baseModalInfo.contentOptions.accepted = accepted;
                        baseModalInfo.contentOptions.migrationError = true;
                        baseModalInfo.contentOptions.errorCode = errorCode;
                        return of(baseModalInfo)
                    })
                );
            }
        }
        // Old behavior for all other cases
        else if (
            service && groupId && service === QueryParamsValues.JOIN_GROUP &&
            flow !== this.valuesService.onboardingScenario.FAMILY_MIGRATION
        ) {
            this.queryParamsService.remove([
                this.valuesService.queryParams.service,
                this.valuesService.queryParams.groupId,
                this.valuesService.queryParams.flow,
                this.valuesService.queryParams.groupType
            ]);
            if (this.profilesService.ownerHasSubsV4()) {
                return this._processAutomaticJoinGroup(groupId, groupType);
            }
        }
        return of(false);
    }
}
